import { ChevronRightIcon } from "@chakra-ui/icons";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Card,
  CardBody,
  CardHeader,
  Text,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { InvestorTransferBackdatedWarningBanner } from "features/investor-transfer/InvestorTransferBackdatedWarningBanner";
import { InvestorTransferDetailsFormFields } from "features/investor-transfer/InvestorTransferDetailsFormFields";
import {
  getInvestorTransferorsForInvestorTransferListQueryKey,
  useInvestorTransferUpsert,
} from "gen/endpoints/investor-transfer/investor-transfer";
import { useLatestTransactionGeneratingFundFamilyEventRetrieveSuspense } from "gen/endpoints/latest-transaction-generating-fund-family-event/latest-transaction-generating-fund-family-event";
import { InvestorTransferReadData } from "gen/model/investorTransferReadData";
import { InvestorTransferUpsertDataRequest } from "gen/model/investorTransferUpsertDataRequest";
import { investorTransferUpsertBody } from "gen/schemas/investor-transfer/investor-transfer";
import React from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import {
  FormFooter,
  FormFooterActions,
  FormFooterBreadcrumb,
} from "shared/components/form/FormFooter";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";

export const InvestorTransferCreatePage: React.FC = () => {
  const { t } = useTranslation(["investorTransfer", "common"]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { fundFamilyId } = useRequiredStringParams();

  const form = useForm<InvestorTransferUpsertDataRequest>({
    resolver: zodResolver(investorTransferUpsertBody),
    defaultValues: {
      fund_family_id: fundFamilyId,
    },
  });

  const { data: latestEventData } =
    useLatestTransactionGeneratingFundFamilyEventRetrieveSuspense(
      fundFamilyId,
      {},
    );

  const recordedDate = useWatch({
    control: form.control,
    name: "recorded_date",
  });

  const { mutateAsync: upsertInvestorTransfer } = useInvestorTransferUpsert();

  return (
    <PageContent contentWidth="boxed">
      <Form
        form={form}
        onSubmit={async (data) => {
          const transfer = await upsertInvestorTransfer({ fundFamilyId, data });
          await queryClient.invalidateQueries({
            queryKey: getInvestorTransferorsForInvestorTransferListQueryKey(
              fundFamilyId,
              transfer.id,
            ),
          });
          return transfer;
        }}
        onSuccess={(response: InvestorTransferReadData) => {
          navigate(
            rootRoute({})
              .fundFamilies({})
              .fundFamily({ fundFamilyId })
              .investorTransfers({})
              .setup({ investorTransferId: response.id })
              .transferees({}).$,
            {
              replace: true,
            },
          );
        }}
      >
        <PageHeader
          workflowName={t("investorTransfer:setup.superHeading")}
          title={t("investorTransfer:setup.details.title")}
          size="lg"
        />
        <PageBody>
          {latestEventData.latest_event_date_plus_one && recordedDate && (
            <InvestorTransferBackdatedWarningBanner
              recordedDate={recordedDate}
              calculatedDate={latestEventData.latest_event_date_plus_one}
            />
          )}
          <Card variant="form">
            <CardHeader>
              <Text textStyle="bodyBold">
                {t("investorTransfer:transferDetails")}
              </Text>
            </CardHeader>
            <CardBody>
              <InvestorTransferDetailsFormFields
                fundFamilyId={fundFamilyId}
                form={form}
              />
            </CardBody>
          </Card>
        </PageBody>
        <FormFooter>
          <FormFooterBreadcrumb>
            <Breadcrumb
              separator={<ChevronRightIcon />}
              data-ls-enabled={true}
              data-public={true}
            >
              {[
                t("investorTransfer:setup.details.breadcrumb"),
                t("investorTransfer:setup.transferees.breadcrumb"),
                t("investorTransfer:setup.documents.breadcrumb"),
                t("investorTransfer:setup.review.breadcrumb"),
                t("investorTransfer:setup.finalize.breadcrumb"),
              ].map((title, index) => (
                <BreadcrumbItem key={index} isCurrentPage={index === 0}>
                  <BreadcrumbLink aria-disabled={true}>{title}</BreadcrumbLink>
                </BreadcrumbItem>
              ))}
            </Breadcrumb>
          </FormFooterBreadcrumb>
          <FormFooterActions>
            <FormCancelButton
              onClick={() => {
                navigate(
                  rootRoute({})
                    .fundFamilies({})
                    .fundFamily({ fundFamilyId })
                    .commitments({})
                    .transfersTab({}).$,
                );
              }}
            />
            <FormSubmitButton>{t("common:continue")}</FormSubmitButton>
          </FormFooterActions>
        </FormFooter>
      </Form>
    </PageContent>
  );
};
